import React, { useEffect, useMemo, useState } from 'react';
import { Collapse } from 'reactstrap';
import { RouteFormatter } from '../../routes';
import useMobileScreen from '../../services/hooks/useMobileScreen';
import MobileSubPage from '../mobile-sub-page';
import ModalMobile from '../modal-mobile';
import SvgChevronRight from 'src/assets/svg/SvgChevronRight.svg';
import SvgMinus from 'src/assets/svg/SvgMinus.svg';
import SvgPlus from 'src/assets/svg/SvgPlus.svg';
import './index.scss';

const FAQS = (isDesktop) => [
    {
        que: 'Is my booking private?',
        ans: () => (
            <>
                Yes! All Sniffspot reservations are private. During your reserved time, no other dogs, other domestic animals, or people can be in the
                spot besides those you bring with you.
            </>
        ),
    },
    {
        que: 'Do I need a subscription (Sniffpass) to book?',
        ans: () => (
            <>
                Yes. Start your Sniffpass free trial or subscription to get prepaid credits toward your bookings and discounts. Learn more about
                Sniffpass{' '}
                <a href={RouteFormatter.helpCenter.sniffpassGuest()} target={isDesktop ? '_blank' : '_self'} rel="noreferrer">
                    here
                </a>
            </>
        ),
    },
    {
        que: 'How do I pay? Can I pay cash or directly?',
        ans: () => <>All payments must go through Sniffspot. Cash, Venmo, PayPal, or direct payments to hosts are not allowed.</>,
    },
    { que: 'Is the yard/space fenced?', ans: () => <>Check the listing details for fence type, height, and whether it's fully fenced or not.</> },
    {
        que: 'Are there other dogs or animals around?',
        ans: () => (
            <>
                Your reservation is always private with no other dogs or other domestic animals like pets or farm animals in the spot. Some hosts may
                have dogs or domestic animals nearby, and wildlife may be present — check the listing notes for details.
            </>
        ),
    },
    {
        que: 'Can children come?',
        ans: () => (
            <>
                Yes, children are welcome unless the listing specifically says otherwise. Please supervise children closely at all times, especially
                around dogs and water.
            </>
        ),
    },
    {
        que: 'What if it rains or I need to reschedule?',
        ans: () => (
            <>
                You can reschedule or cancel for a full refund up to 2 hours before the start time.{' '}
                <a href={RouteFormatter.helpCenter.editRes()} target={isDesktop ? '_blank' : '_self'} rel="noreferrer">
                    Here's how
                </a>{' '}
                to modify your booking right from the app!
            </>
        ),
    },
    {
        que: 'Are dogs required to be vaccinated?',
        ans: () => <>Yes. Sniffspot requires all visiting dogs to be vaccinated in compliance with local laws and regulations.</>,
    },
    {
        que: 'Can I host a party, training session, or group event?',
        ans: () => (
            <>
                Some hosts allow or even make special accommodations for small gatherings like birthday parties or training groups, while others do
                not. Please check the listing or message the host before booking a large group.
            </>
        ),
    },
    {
        que: 'Is there parking?',
        ans: () => (
            <>
                Yes. Parking details are included in each listing description. If you're bringing a large vehicle (such as a van or RV), check the
                notes or ask the host if the space can accommodate it.
            </>
        ),
    },
    {
        que: 'What should I bring?',
        ans: () => (
            <>
                Bring a leash, water, waste bags, and anything you and your dog need to be comfortable. Please pick up after your dog and leave the
                space how you found it for the next guest.
            </>
        ),
    },
];

const RenderFaqs = ({ isDesktop }) => {
    const [collapse, setCollapse] = useState(null);

    const myFaqs = useMemo(() => FAQS(isDesktop), [isDesktop]);

    const toggle = (i) => {
        if (!collapse !== null) {
            if (collapse === i) {
                setCollapse(null);
            } else {
                setCollapse(i);
            }
        } else {
            setCollapse(i);
        }
    };

    return (
        <div className="spot-details-faqs-list">
            {myFaqs.map((v, i) => {
                const isActiveIdx = collapse == i;
                return (
                    <div
                        key={i}
                        className={`faq-block ${isActiveIdx ? 'active' : ''}`}
                        onClick={() => toggle(i)}
                        itemScope
                        itemProp="mainEntity"
                        itemType="https://schema.org/Question"
                    >
                        <div className={`faq-title snif-m2 snif-medium ${isActiveIdx ? 'active' : ''}`}>
                            <div className="title-text" itemProp="name">
                                {v.que}
                            </div>
                            <div className="faq-icon">{isActiveIdx ? <SvgMinus /> : <SvgPlus />}</div>
                        </div>
                        <Collapse isOpen={isActiveIdx}>
                            <div className="faq-subtitle snif-p text-deep" itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                                <div itemProp="text">
                                    <v.ans />
                                </div>
                            </div>
                        </Collapse>
                        <div className="pb-3" />
                    </div>
                );
            })}
        </div>
    );
};

const RenderListingFaqs = ({ isSpotFaqsTest, isFaqOpen, toggleFaq }) => {
    const isMobile = useMobileScreen();
    const [isDesktop, setIsDesktop] = useState(false);

    useEffect(() => {
        if (!isMobile) {
            setIsDesktop(true);
        }
    }, []);

    if (!isSpotFaqsTest) {
        return '';
    }

    return (
        <div className="spot-details-faqs" itemScope itemType="https://schema.org/FAQPage">
            <h2 className="d-flex justify-content-between align-items-center snif-m1 snif-semibold my-3 c-pointer" onClick={toggleFaq}>
                Sniffspot FAQs
                <SvgChevronRight className="text-soft me-2" width={8} height={14} />
            </h2>
            {isDesktop ? (
                <ModalMobile title="Sniffspot FAQs" isOpen={isFaqOpen} toggle={toggleFaq} className="spot-details-faqs-list_modal">
                    <RenderFaqs {...{ isDesktop }} />
                </ModalMobile>
            ) : (
                <MobileSubPage title="Sniffspot FAQs" show={isFaqOpen} onBack={toggleFaq} className="spot-details-faqs-list_modal">
                    <RenderFaqs {...{ isDesktop }} />
                </MobileSubPage>
            )}
            <hr />
        </div>
    );
};

export default RenderListingFaqs;
